
import {
  CoordinatesGetters,
  DefaultMapCoordinates,
  FilterKey,
  ICoordinates,
  IVacancy,
  IVacancyMapPoint,
  LanguageLocale,
  LocalizationData,
  LocalizationPath,
  UserAgent,
  VacancyMapQueryDto
} from '@tokyojob/core'
import {
  IButton,
  ButtonFactory,
  CButton,
  CFilterTagList,
  CIconFactory,
  CLoader,
  FilterGroupFactory,
  FilterQueryManager,
  FilterTagFactory,
  getLocalization,
  IconSize,
  IconType,
  IFilterGroup,
  IFilterTag,
  ISelectedFilterMultiOptions,
  IVacancyFilterTreeData,
  MapBoundsGetters,
  VacancyFilterBuilder,
  getVacancyTreeFilterData
} from '@tokyojob/frontend-components'
import {
  getVacancyMapStore,
  IFormInputData,
  IHead,
  LocalizationService,
  QueryStringEntityFactory,
  VacancyHasAppliedComposable,
  VacancyMapMetaFactory
} from '@tokyojob/frontend-core'
import Vue from 'vue'
import { Layout } from '~/base/utils/constants/layout'
import authenticatedOptional from '~/middleware/authenticated-optional'
import CFilterSideMenu from '~/modules/app/components/c-filter/c-filter-side-menu/c-filter-side-menu.component.vue'
import CFilter from '~/modules/app/components/c-filter/c-filter.component.vue'
import { ISelectedVacancyFilters } from '~/modules/app/components/c-filter/interface/i-selected-vacancy-filters'
import { SelectedVacancyFiltersDomain } from '~/modules/app/components/c-filter/util/selected-vacancy-filters-domain'
import CVacancyCardSmall from '~/modules/auth/components/candidate/c-vacancy-card-small/c-vacancy-card-small.component.vue'
import CVacancyMapFrame from '~/modules/auth/components/candidate/c-vacancy-map/c-vacancy-map-frame.component.vue'
import CandidateVisaUsecase from '~/modules/user/usecases/candidate-visa.usecase'
import FetchMapVacanciesUseCase from '~/modules/vacancy/usecases/vacancy-map.usecase'
import VisaTypeGetUsecase from '~/modules/vacancy/usecases/visa-type-get.usecase'
import { IApp } from '~/types/nuxt'

const filterQueryManager = new FilterQueryManager()
const selectedVacancyFiltersDomain = new SelectedVacancyFiltersDomain(filterQueryManager, new FilterTagFactory(), {}, {})

interface IData {
  /**
   * Language passed to confirmation modal.
   */
  language: LanguageLocale
  /**
   * Array of all possible Visa Types
   */
  visaTypes: IFormInputData[]
  /**
   * Array of all currently selected Visas
   */
  selectedVisaTypes: IFormInputData[]
  /**
   * The currently available points
   */
  vacancyPoints: IVacancyMapPoint[]
  /**
   * The string id of the vacancy map
   */
  mapId: string
  /**
   * The default coordinate of the map
   */
  centerCoordinates: ICoordinates
  /**
   * True if the current resolution is for mobile. Otherwise this will be false.
   */
  isMobile: boolean
  /**
   *
   */
  selectedCard: InstanceType<typeof CVacancyCardSmall> | undefined
  /**
   *
   */
  vacancyMapFrame: InstanceType<typeof CVacancyMapFrame> | undefined
  /**
   * Array of IFilter objects
   */
  filters: IFilterGroup
  /**
   * Object containing results for each of the filters
   */
  selectedFiltersData: ISelectedVacancyFilters
  /**
   * Object containing hierarchical data for the c-filter
   */
  vacancyFilterTreeData: IVacancyFilterTreeData
  /**
   * The filter tags to display in the UI, below the filters component.
   * These should react to changes in the querystring, and fire close events to trigger a filter's data reset.
   */
  filterTags: IFilterTag[]
  /**
   * Whether the filter side menu is open or not
   * Only used on screen sizes below 768px
   */
  filterSideMenuOpen: boolean
  /**
   * Button to open filter side menu when on screen width below 768px
   */
  filterSideMenuBtn: IButton
  /**
   * Dictionary of which filter popouts components are open
   */
  popoutdictionary: Record<string, boolean>
  /**
   * True, if the mounted initialization is already done
   */
  hasMountedInitialized: boolean
  /**
   * Display the loading overlay
   */
  isLoading: boolean
  /**
   * The string to display when there are no jobs
   */
  noJobsStr: string
  /**
   * localization info from the context
   */
  localization: LocalizationData
  /**
   * If top filter bar should be displayed on mobile
   */
  showFilterBar: boolean
  /**
   * Returns true if the passed vacancy id has been applied to, otherwise return false
   */
  hasApplied: (vacancyId: number | undefined) => void

  App: IApp
}

export default Vue.extend({
  layout: Layout.Home,
  middleware: [authenticatedOptional],
  components: {
    CVacancyMapFrame,
    CVacancyCardSmall,
    CFilter,
    CFilterTagList,
    CFilterSideMenu,
    CButton,
    CLoader
  },
  async asyncData({ App, route, redirect }) {
    const locale = App.localizationSelector.languageLocale

    // We fetch the vacancy filter information
    const vacancyFilterTreeData: IVacancyFilterTreeData = {}
    const vacancyTreeFilterDataStore = getVacancyTreeFilterData()
    vacancyFilterTreeData[FilterKey.category] = vacancyTreeFilterDataStore.get(FilterKey.category)
    vacancyFilterTreeData[FilterKey.visa] = vacancyTreeFilterDataStore.get(FilterKey.visa)
    vacancyFilterTreeData[FilterKey.location] = vacancyTreeFilterDataStore.get(FilterKey.location)

    // Set the vacancy filter information to selectedVacancyFiltersDomain
    selectedVacancyFiltersDomain.setTreeData(vacancyFilterTreeData)

    // We filter out the invalid information in the query string and redirect the user
    const result = selectedVacancyFiltersDomain.filterInvalidQuery(App, route, process.server, '/job/map', redirect)
    if (result.hasRemovedAQuery) return

    const visaTypesUseCase = new VisaTypeGetUsecase(App)
    const visaTypes = await visaTypesUseCase.execute()
    const candidateVisaUsecase = new CandidateVisaUsecase(App)
    const candidateVisa = await candidateVisaUsecase.execute()

    const filtersBuilder = new VacancyFilterBuilder()
    filtersBuilder.candidateVisa = { visaType: candidateVisa?.props.visaType, visaSubType: candidateVisa?.props.visaSubType }
    filtersBuilder.query = route.query
    filtersBuilder.visaTypes = visaTypes
    let selectedVisaTypesDict: Dictionary<IFormInputData> = filtersBuilder.build()
    const selectedVisaTypes = Object.values(selectedVisaTypesDict)
    const mapId = 'vacancy-map'
    const centerCoordinates = DefaultMapCoordinates
    const localization: LocalizationData = getLocalization(App as IApp)
    const filters = FilterGroupFactory.VacancyMapSearchFilterGroup(localization, vacancyFilterTreeData)
    const popoutdictionary = {} as Record<string, boolean>
    for (const filter in filters.filters) {
      const name = filters.filters[filter].name
      popoutdictionary[name] = false
    }

    let query = {}
    if (process.server) query = route.query
    else {
      const formattedFilters = App.state.formattedFilters
      if (formattedFilters && formattedFilters != '') query = JSON.parse(formattedFilters)
    }
    const selectedFiltersData = selectedVacancyFiltersDomain.fromQuery(query)
    const filterTags = selectedVacancyFiltersDomain.createFilterTags(localization, selectedFiltersData, [FilterKey.location])

    return {
      selectedCard: undefined,
      vacancyMapFrame: undefined,
      language: LanguageLocale.USen,
      visaTypes,
      selectedVisaTypes,
      vacancyPoints: [] as IVacancyMapPoint[],
      mapId,
      centerCoordinates,
      vacancyFilterTreeData,
      filters,
      selectedFiltersData,
      popoutdictionary,
      filterTags
    }
  },
  data(): IData {
    let query = {}
    const localization: LocalizationData = getLocalization(this.App as IApp)
    if (process.server) query = this.$route.query
    else {
      const formattedFilters = this.App.state.formattedFilters
      if (formattedFilters && formattedFilters != '') query = JSON.parse(formattedFilters)
    }

    const selectedFiltersData = selectedVacancyFiltersDomain.fromQuery(query)
    const filterTags = selectedVacancyFiltersDomain.createFilterTags(localization, selectedFiltersData, [FilterKey.location])
    const filterSideMenuBtn = ButtonFactory.button({
      icon: CIconFactory.Icon({ icon: 'magnifying-glass', type: IconType.solid, size: IconSize.large }),
      classes: 'row-between align-center ph-15 pv-5 theme-shades-dark1-color br-10 filter-button-text-size',
      label: LocalizationService.t<string>(localization, LocalizationPath.filterSideMenuMap),
      cyTag: 'c-filter-sidemenu-button'
    })

    const noJobsStr = LocalizationService.t<string>(localization, LocalizationPath.noJobsFound)

    const { hasApplied } = VacancyHasAppliedComposable()

    return {
      popoutdictionary: {},
      selectedCard: undefined,
      vacancyMapFrame: undefined,
      language: LanguageLocale.USen,
      visaTypes: [],
      selectedVisaTypes: [],
      vacancyPoints: [] as IVacancyMapPoint[],
      mapId: 'vacancy-map',
      centerCoordinates: DefaultMapCoordinates,
      isMobile: false,
      selectedFiltersData,
      vacancyFilterTreeData: {} as IVacancyFilterTreeData,
      filters: {} as IFilterGroup,
      filterTags,
      filterSideMenuBtn,
      filterSideMenuOpen: false,
      showFilterBar: true,
      hasMountedInitialized: false,
      isLoading: false,
      noJobsStr,
      localization,
      hasApplied,
      App: this.App
    }
  },
  computed: {
    /**
     * Assess whether the user is on a small screen and high filter when info window in use
     */
    checkFilterBar(): boolean {
      return this.showFilterBar || window.innerHeight > 750
    },
    canDisplayCardList(): boolean {
      const notMobileAndHasVacancyPoints = this.vacancyPoints && this.vacancyPoints.length > 0
      return !!notMobileAndHasVacancyPoints
    },
    SelectedFiltersData: {
      set(value: ISelectedVacancyFilters) {
        this.selectedFiltersData = value
        selectedVacancyFiltersDomain.setTreeData(this.vacancyFilterTreeData)
        this.filterTags = selectedVacancyFiltersDomain.createFilterTags(this.localization, this.SelectedFiltersData, [
          FilterKey.location
        ])
        /**
         * We only touch the URL query when the mounted initialization is done
         * because Vue will throw us a warning that we attempted to modify the URL query twice in the same frame
         */
        if (this.hasMountedInitialized) {
          const query = selectedVacancyFiltersDomain.toQuery(value)
          // We need to preserve the query data that is not part of the vacancy filter query data
          // ex. lowerLeftLng -> map bounds data
          const additionalQuery = filterQueryManager.extractExcessDataFromQuery(this.$route.query)
          filterQueryManager.modifyQuery(query, this.App, this.$router, this.$route, additionalQuery)
        }
        // Removed because the query string will update after this which will trigger updatePoints
        // this.updatePoints()
      },
      get(): ISelectedVacancyFilters {
        return this.selectedFiltersData
      }
    }
  },
  watch: {
    /**
     * Watches the route for filter changes.
     */
    async $route({ query }): Promise<void> {
      await this.updatePoints(query)
      if (this.selectedCard) this.scrollToCard(this.selectedCard)
    }
  },
  async mounted(): Promise<void> {
    filterQueryManager.initialize(this.App, this.$router, this.$route)
    this.isMobile = UserAgent.isMobile(navigator.userAgent)
    // 1 Load at the user's current location if they already accepted the GPS request
    if (MapBoundsGetters.hasBounds(this.$route.query)) {
      await this.updatePoints(this.$route.query)
    }
    this.hasMountedInitialized = true
  },
  methods: {
    /**
     * Fetch all points for the current list of vacancies
     */
    async updatePoints(query: Dictionary<string | (string | null)[]>): Promise<void> {
      ;(this.$refs as any).loadingOverlay.show()
      const dto = new VacancyMapQueryDto()
      // Adjust decimal precision as needed
      dto.decimalPrecision = 3
      dto.visaTypeId = query.visaTypeId as any
      const pointsUsecase = new FetchMapVacanciesUseCase(this.App)
      const bounds = MapBoundsGetters.getBounds(query, this.isMobile)
      dto.lowerLeftLng = bounds.lowerLeftLng
      dto.lowerLeftLat = bounds.lowerLeftLat
      dto.upperRightLng = bounds.upperRightLng
      dto.upperRightLat = bounds.upperRightLat
      dto.locale = this.App.localizationSelector.languageLocale
      dto.sessionId = ''
      dto.isSSRRequest = process.server.toString()
      await pointsUsecase.execute(dto, QueryStringEntityFactory(query))
      ;(this.$refs as any).loadingOverlay.hide()
      const vacancyMap = getVacancyMapStore()
      this.vacancyPoints = vacancyMap.all()
    },
    updateCenterCoordinates(coordinates: ICoordinates): void {
      this.centerCoordinates = coordinates
    },
    getCoordinatesString: CoordinatesGetters.getCoordinatesString,
    /**
     * Toggles the highlight of vacancy cards
     */
    onMarkerClicked(marker: google.maps.Marker): void {
      const point = (marker as any).customData.point as IVacancyMapPoint
      // When the marker is first clicked, just use the first available item
      const job = point.vacancies[0]
      if (!job) return
      this.toggleCard(job)
    },
    /**
     * Hide the filter bar when a mobile sized card is opened
     */
    onHideFilterBar(): void {
      this.showFilterBar = false
    },
    /**
     * Hide the filter bar when a mobile sized card is opened
     */
    onShowFilterBar(): void {
      this.showFilterBar = true
    },
    /**
     * Triggered when the card that is being shown in the map info window changes.
     */
    onDisplayedVacancyChange(vacancy: IVacancy): void {
      this.toggleCard(vacancy)
    },
    /**
     * Toggles the card of the provided vacancy to be highlighted.
     * The previously highlighted card is unhighlighted.
     */
    toggleCard(vacancy: IVacancy): void {
      if (!vacancy) return
      const refId = this.getCardRef(vacancy)
      const cardRefs = this.$refs[refId]
      const card = cardRefs[0] as InstanceType<typeof CVacancyCardSmall>
      if (!card) return
      if (this.selectedCard) this.selectedCard.selected = false
      card.selected = true
      this.selectedCard = card
      this.scrollToCard(card)
    },
    /**
     * Triggers a scroll within the card list, into the provided card.
     */
    scrollToCard(card: InstanceType<typeof CVacancyCardSmall>): void {
      const cardElement = document.getElementById(card.id)
      const cardList = document.getElementById('card-list')
      if (!cardElement) return
      const top = cardElement!.offsetTop - cardList!.offsetTop
      if (!cardList) return
      cardList.scrollTo({
        top: top,
        behavior: 'smooth'
      })
    },
    /**
     * Gets the ref that a card should have, for later retrieval from the DOM.
     */
    getCardRef(vacancyMap: IVacancy): string {
      return `vacancyCard${vacancyMap.id}`
    },
    /**
     * Triggers when a card is clicked. Should highlight and center on in the
     * marker that contains that vacancy, and open its info window.
     */
    onCardClicked(item: IVacancy, point: IVacancyMapPoint): void {
      if (!this.vacancyMapFrame) {
        const mapFrame = this.$refs['vacancyMapFrame'] as InstanceType<typeof CVacancyMapFrame>
        this.vacancyMapFrame = mapFrame
      }
      if (this.selectedCard) this.selectedCard.selected = false
      const refId = this.getCardRef(item)
      const cardRefs = this.$refs[refId]
      const card = cardRefs[0] as InstanceType<typeof CVacancyCardSmall>
      if (!card) return
      card.selected = true
      this.selectedCard = card
      /**
       * @todo May not be needed. Just testing as we saw some strange behaviour with test
       * data being off centre. - Will
       */

      this.updateCenterCoordinates(point.coordinates)
      this.vacancyMapFrame.highlightMarker(item, point)
      this.toggleCard(item)
    },
    /**
     * Called when an item in the filter selection component was selected
     */
    handleItemSelected(data: string, category: string): void {
      this.SelectedFiltersData = selectedVacancyFiltersDomain.addDataToISelectedVacancyFilters(
        this.SelectedFiltersData,
        category as FilterKey,
        data
      )
    },
    /**
     * Updates the filter data with the new multi filter values.
     */
    onMultiFilterApplied(newFilter: ISelectedFilterMultiOptions): void {
      // TODO: update this to use the SelectedVacancyFiltersDomain.addDataToISelectedVacancyFilters
      this.SelectedFiltersData = { ...this.SelectedFiltersData, multiOption: newFilter }
    },
    /**
     * Updates the filter data with the new jp level filter values.
     */
    onJPLevelFilterApplied(newJPLevelFilterSelection: number): void {
      this.SelectedFiltersData = { ...this.SelectedFiltersData, japaneseLevel: newJPLevelFilterSelection }
    },
    /**
     * Called when a filter tag was removed
     */
    onTagClose(value: IFilterTag): void {
      this.SelectedFiltersData = selectedVacancyFiltersDomain.removeSelection(
        this.SelectedFiltersData,
        value.filterKey! as FilterKey
      )
    },
    /**
     * Refreshes the list of filter tags
     */
    refreshFilterTags(): void {
      this.filterTags = selectedVacancyFiltersDomain.createFilterTags(this.localization, this.selectedFiltersData, [
        FilterKey.location
      ])
    },
    togglePopout(name: string, status: boolean): void {
      for (const item in this.popoutdictionary) {
        if (item === name) {
          this.popoutdictionary[item] = status
        } else {
          this.popoutdictionary[item] = false
        }
      }
    },
    /**
     * Open the dide menu when on small screen sizes
     */
    closeSideMenu(): void {
      this.filterSideMenuOpen = false
      document.getElementById('__nuxt')!.classList.remove('overflow-hidden')
    },
    /**
     * Receive new selected tags from side menu then close side menu
     */
    saveAndCloseSideMenu(selectedFiltersDataSideMenu: ISelectedVacancyFilters) {
      this.SelectedFiltersData = selectedFiltersDataSideMenu
      this.closeSideMenu()
    },
    /**
     * Open the dide menu when on small screen sizes
     */
    openSideMenu(): void {
      this.filterSideMenuOpen = true
      document.getElementById('__nuxt')!.classList.add('overflow-hidden')
    }
  },
  head(): IHead {
    const localization = getLocalization(this.App)
    const title = LocalizationService.t<string>(localization, LocalizationPath.VacancyMapPageMetaTagTitle)
    const meta = VacancyMapMetaFactory.metaTags(localization, this.App.localizationSelector.languageLocale)

    return { title, meta }
  }
})
